import React, { useEffect, useState } from 'react';
import api from '../api/axios';
import { X } from 'lucide-react';

const TYPE_CONFIG = {
  'Congé':   { bg: 'bg-blue-100',  text: 'text-blue-700',  border: 'border-blue-200'  },
  'Maladie': { bg: 'bg-red-100',   text: 'text-red-700',   border: 'border-red-200'   },
  'Autre':   { bg: 'bg-slate-100', text: 'text-slate-700', border: 'border-slate-200' },
};

const TYPES = ['Congé', 'Maladie', 'Autre'];

const formatDate = (iso) => {
  const d = new Date(iso);
  const dd = String(d.getUTCDate()).padStart(2, '0');
  const mm = String(d.getUTCMonth() + 1).padStart(2, '0');
  const yyyy = d.getUTCFullYear();
  return `${dd}/${mm}/${yyyy}`;
};

const today = () => new Date().toISOString().split('T')[0];

const EmployeeLeaveModal = ({ employee, onClose }) => {
  const [leaves, setLeaves]         = useState([]);
  const [loadingLeaves, setLoadingLeaves] = useState(true);

  // Formulaire
  const [type, setType]             = useState('Congé');
  const [dateDebut, setDateDebut]   = useState('');
  const [dateFin, setDateFin]       = useState('');
  const [note, setNote]             = useState('');
  const [formError, setFormError]   = useState('');
  const [submitting, setSubmitting] = useState(false);

  const fetchLeaves = async () => {
    setLoadingLeaves(true);
    try {
      const res = await api.get(`/admin/leaves?employeeId=${employee._id}`);
      setLeaves(res.data.data || []);
    } catch {
      setLeaves([]);
    } finally {
      setLoadingLeaves(false);
    }
  };

  useEffect(() => { fetchLeaves(); }, [employee._id]);

  const handleAdd = async () => {
    setFormError('');
    if (!dateDebut || !dateFin) { setFormError('Les dates de début et de fin sont obligatoires.'); return; }
    if (dateDebut > dateFin)    { setFormError('La date de début doit être antérieure ou égale à la date de fin.'); return; }
    setSubmitting(true);
    try {
      await api.post('/admin/leaves', { employeeId: employee._id, dateDebut, dateFin, type, note });
      setDateDebut(''); setDateFin(''); setNote(''); setType('Congé');
      await fetchLeaves();
    } catch (err) {
      setFormError(err.response?.data?.message || "Erreur lors de l'ajout.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (leaveId) => {
    try {
      await api.delete(`/admin/leaves/${leaveId}`);
      await fetchLeaves();
    } catch {
      // silently ignore
    }
  };

  return (
    <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm flex items-end sm:items-center justify-center z-[60] p-0 sm:p-4 animate-in fade-in duration-200">
      <div className="bg-white rounded-t-[2.5rem] sm:rounded-[2.5rem] w-full max-w-md shadow-2xl relative max-h-[92vh] flex flex-col">

        {/* Header */}
        <div className="p-6 pb-4 border-b border-slate-50 shrink-0">
          <button
            onClick={onClose}
            className="absolute top-5 right-5 text-slate-300 hover:text-slate-600 transition-colors p-1"
          >
            <X size={22} />
          </button>
          <h2 className="text-xl font-black text-slate-900">
            Congés - {employee.prenom} {employee.nom}
          </h2>
          <p className="text-slate-400 text-sm font-medium mt-1">Gérer les absences de cet employé</p>
        </div>

        {/* Scrollable body */}
        <div className="flex-1 overflow-y-auto px-6 py-4 space-y-5">

          {/* Liste des congés existants */}
          <div className="space-y-2">
            <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Absences enregistrées</p>
            {loadingLeaves ? (
              <div className="flex justify-center py-6">
                <div className="w-6 h-6 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
              </div>
            ) : leaves.length === 0 ? (
              <p className="text-slate-400 text-sm font-medium italic py-4 text-center">
                Aucune absence enregistrée
              </p>
            ) : (
              leaves.map((leave) => {
                const cfg = TYPE_CONFIG[leave.type] || TYPE_CONFIG['Autre'];
                return (
                  <div key={leave._id}
                    className={`flex items-center justify-between gap-3 p-3.5 rounded-2xl border ${cfg.bg} ${cfg.border}`}>
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className={`text-xs font-black ${cfg.text}`}>{leave.type}</span>
                        <span className={`text-xs font-bold ${cfg.text} opacity-80`}>
                          {formatDate(leave.dateDebut)} → {formatDate(leave.dateFin)}
                        </span>
                      </div>
                      {leave.note && (
                        <p className={`text-[11px] font-medium mt-0.5 truncate ${cfg.text} opacity-70`}>
                          {leave.note}
                        </p>
                      )}
                    </div>
                    <button
                      onClick={() => handleDelete(leave._id)}
                      className="shrink-0 p-1.5 text-slate-400 hover:text-red-500 hover:bg-white/60 rounded-lg transition-all"
                      title="Supprimer ce congé"
                    >
                      <X size={15} />
                    </button>
                  </div>
                );
              })
            )}
          </div>

          {/* Formulaire d'ajout */}
          <div className="space-y-3 pt-2 border-t border-slate-50">
            <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Ajouter une absence</p>

            {/* Pills type */}
            <div className="flex items-center gap-2 flex-wrap">
              {TYPES.map(t => (
                <button
                  key={t}
                  type="button"
                  onClick={() => setType(t)}
                  className={`px-4 py-2 rounded-xl font-black text-xs transition-all border ${
                    type === t
                      ? t === 'Congé'   ? 'bg-blue-600 text-white border-blue-600 shadow-md'
                      : t === 'Maladie' ? 'bg-red-500 text-white border-red-500 shadow-md'
                      :                   'bg-slate-700 text-white border-slate-700 shadow-md'
                      : 'bg-slate-50 text-slate-500 border-slate-100 hover:border-slate-200'
                  }`}
                >
                  {t}
                </button>
              ))}
            </div>

            {/* Dates */}
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <label className="text-[10px] font-black text-slate-400 uppercase px-1">Début</label>
                <input
                  type="date"
                  min={today()}
                  value={dateDebut}
                  onChange={e => { setDateDebut(e.target.value); setFormError(''); }}
                  className="w-full p-3 bg-slate-50 rounded-2xl outline-none focus:ring-2 ring-blue-500/10 font-bold text-slate-700 text-sm"
                />
              </div>
              <div className="space-y-1">
                <label className="text-[10px] font-black text-slate-400 uppercase px-1">Fin</label>
                <input
                  type="date"
                  min={today()}
                  value={dateFin}
                  onChange={e => { setDateFin(e.target.value); setFormError(''); }}
                  className="w-full p-3 bg-slate-50 rounded-2xl outline-none focus:ring-2 ring-blue-500/10 font-bold text-slate-700 text-sm"
                />
              </div>
            </div>

            {/* Note */}
            <div className="space-y-1">
              <label className="text-[10px] font-black text-slate-400 uppercase px-1">Note</label>
              <textarea
                rows={2}
                placeholder="Optionnel"
                value={note}
                onChange={e => setNote(e.target.value)}
                className="w-full p-3 bg-slate-50 rounded-2xl outline-none focus:ring-2 ring-blue-500/10 font-medium text-slate-700 text-sm resize-none"
              />
            </div>

            {/* Erreur */}
            {formError && (
              <p className="text-red-600 text-xs font-bold bg-red-50 border border-red-100 rounded-xl px-3 py-2">
                {formError}
              </p>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="p-6 pt-4 border-t border-slate-50 shrink-0">
          <button
            onClick={handleAdd}
            disabled={!dateDebut || !dateFin || submitting}
            className="w-full p-4 bg-slate-900 text-white rounded-2xl font-black flex items-center justify-center hover:bg-black transition-all active:scale-[0.98] disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {submitting ? (
              <>
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                Enregistrement…
              </>
            ) : (
              'Ajouter'
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default EmployeeLeaveModal;
